import './ui/styles.css';
import { AppShell } from './ui/app';
import { GalaxyView } from './ui/galaxy';
import { loadGame, saveGame, wipeSave } from './save';
import { advance } from './sim/tick';
import { fleetStats } from './sim/stats';
import { OFFLINE_MIN_SECONDS, simulateOffline } from './sim/offline';
import { newGame, type GameState } from './sim/state';
import type { SimEvent } from './sim/events';
import { initNative } from './native';
import { initPwa } from './pwa';

const STEP = 0.1;
const MAX_STEPS_PER_FRAME = 400;
const UI_INTERVAL_MS = 200;
const AUTOSAVE_MS = 5000;

async function boot(): Promise<void> {
  let state: GameState = loadGame();
  let wiping = false;
  let galaxy: GalaxyView | null = null;

  const shell = new AppShell(document.getElementById('app')!, {
    getState: () => state,
    save: () => saveGame(state),
    wipe: () => {
      wiping = true;
      wipeSave();
      state = newGame();
      saveGame(state);
      wiping = false;
      shell.ui.selected = 0;
      shell.toast('Save wiped', 'bad');
    },
    galaxyShown: () => galaxy?.app.resize(),
  });
  const emit = (e: SimEvent): void => shell.onSimEvent(e);
  // Dev server only: read the live state from the console (`vm()`).
  if (import.meta.env.DEV) (window as unknown as { vm: () => GameState }).vm = () => state;

  /** Catch up a gap: short gaps play through live, long ones get the summary card. */
  const catchUp = (seconds: number): void => {
    if (seconds >= OFFLINE_MIN_SECONDS && state.run.time > 0) {
      shell.showOffline(simulateOffline(state, seconds));
    } else {
      for (let t = 0; t < seconds; t += STEP) advance(state, STEP, emit);
    }
  };

  catchUp((Date.now() - state.savedAt) / 1000);

  galaxy = new GalaxyView(shell.galaxyHost, (i) => shell.tapPlanet(i), () => shell.tapAsteroid());
  await galaxy.init();
  shell.galaxy = galaxy;
  if (import.meta.env.DEV) (window as unknown as { vmGalaxy: unknown }).vmGalaxy = galaxy;

  let acc = 0;
  let lastUi = 0;
  let lastError = '';
  galaxy.app.ticker.add((ticker) => {
    // An exception here would stop Pixi's frame loop for good, freezing the game.
    // Log it once and keep ticking instead.
    try { frame(ticker.deltaMS); } catch (err) {
      const text = String((err as Error)?.stack ?? err);
      if (text !== lastError) { lastError = text; console.error(err); }
    }
  });

  function frame(deltaMS: number): void {
    const speed = shell.ui.devSpeed;
    acc += Math.min(deltaMS / 1000, 1) * speed;
    const step = speed >= 100 ? 1 : STEP;
    let n = 0;
    while (acc >= step && n < MAX_STEPS_PER_FRAME) {
      advance(state, step, emit);
      acc -= step;
      n += 1;
    }
    if (n >= MAX_STEPS_PER_FRAME) acc = 0;
    // The galaxy strip only shows on the Planets tab, never behind the Station.
    if (shell.ui.screen === 'galaxy' && shell.ui.tab === 'planets') galaxy!.draw(state, fleetStats(state), shell.ui.selected);
    const now = performance.now();
    if (now - lastUi >= UI_INTERVAL_MS) {
      lastUi = now;
      shell.refresh();
    }
  }

  setInterval(() => { if (!wiping) saveGame(state); }, AUTOSAVE_MS);

  let hiddenAt = 0;
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      hiddenAt = Date.now();
      saveGame(state);
    } else if (hiddenAt > 0) {
      catchUp((Date.now() - hiddenAt) / 1000);
      hiddenAt = 0;
      shell.refresh();
    }
  });
  window.addEventListener('pagehide', () => saveGame(state));

  void initNative(shell, () => saveGame(state));
  initPwa((apply) => shell.offerUpdate(apply));
  shell.refresh();
}

void boot();
